import React from 'react';
import { useTranslation } from 'react-i18next';
import { SlidersHorizontal, Users, Info } from 'lucide-react';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import Tooltip from '../../components/ui/Tooltip';
import { useAuth } from '../../context/AuthContext';
import {
  PageWrap, FloatingFilters, FilterIcon, Grid, ScopeChip, InfoStrip, Stale, StripStack,
} from './styles';
import { useAttendanceDashboardApi } from './config/useAttendanceDashboardApi';
import { formatIsoDay } from './format';
import PeriodFilter from './components/PeriodFilter';
import KpiRow from './components/KpiRow';
import DailyTrendCard from './components/DailyTrendCard';
import DepartmentBreakdownCard from './components/DepartmentBreakdownCard';
import LateDistributionCard from './components/LateDistributionCard';
import TopPeopleCard from './components/TopPeopleCard';
import BradfordCard from './components/BradfordCard';
import WarningsCard from './components/WarningsCard';

// AKM POC §5.15 — Devam Paneli (/pdks-dashboard; eski PDKS Paneli'nin yerine). İzin Paneli deseni: kart başına ayrı
// uç, yüzen süzgeç çubuğu, kart başına PNG. Yetki: TA yazma tüm şirket; yalnız TA okuma kendi ekibi ("Ekibim" çipi).
// Sayfa gövdesinde başlık yok; tek kaydırma (kartlarda iç kaydırma yok). Süzgeç seçenekleri sunucuda aranır (M9).
export default function AttendanceDashboardPage() {
  const { t, i18n } = useTranslation();
  const { canRead, canWrite } = useAuth();
  const {
    filters, setFilters, query, slices, lookups, statusesAvailable, awaitingRange,
  } = useAttendanceDashboardApi();

  const summary = slices.summary.failed ? null : slices.summary.data;
  const effectiveTo = summary?.effectiveTo || null;
  const closedTo = summary?.closedTo || null;
  // Panelden listeye geçiş yalnız özet yüklendiyse ve dönemin geçmiş günü varsa (gelecek dönemde ters aralık olurdu — M6).
  const drillable = Boolean(summary && !awaitingRange && summary.effectiveTo >= summary.from);
  const isTeam = summary?.scope === 'team';
  // "Ekibinizde kimse yok" süzgeçten bağımsız ekip büyüklüğüne bakar (M2).
  const noTeam = isTeam && summary?.teamHeadcount === 0;
  const beforeRules = Boolean(summary?.rulesEffectiveFrom && summary?.from && summary.from < summary.rulesEffectiveFrom);

  const select = (key) => (value) => setFilters((prev) => ({ ...prev, [key]: value || null }));
  const filterSelect = (key, lookup, placeholderKey) => (
    <AsyncGenericSelect
      value={filters[key]}
      onChange={select(key)}
      onSearch={lookup.onSearch}
      onGetById={lookup.onGetById}
      placeholder={t(placeholderKey)}
      label={t(placeholderKey)}
      minChars={0}
      compact
      direction="top"
    />
  );

  return (
    <PageWrap data-page="attendance-dashboard">
      <FloatingFilters data-export-exclude>
        <FilterIcon><SlidersHorizontal size={14} /></FilterIcon>
        <PeriodFilter filters={filters} setFilters={setFilters} query={query} />
        {statusesAvailable ? filterSelect('employeeStatusId', lookups.statuses, 'attendanceDashboard.filters.allStatuses') : null}
        {filterSelect('userGroupId', lookups.groups, 'attendanceDashboard.filters.allGroups')}
        {filterSelect('departmentId', lookups.departments, 'attendanceDashboard.filters.allDepartments')}
        {filterSelect('locationId', lookups.locations, 'attendanceDashboard.filters.allLocations')}
        {isTeam ? (
          <span data-wide>
            <Tooltip text={t('attendanceDashboard.scope.teamHint')}>
              <ScopeChip data-scope-chip="team">
                <Users size={12} />
                {t('attendanceDashboard.scope.team')}
              </ScopeChip>
            </Tooltip>
          </span>
        ) : null}
      </FloatingFilters>
      {awaitingRange || noTeam || beforeRules ? (
        <StripStack>
          {awaitingRange ? (
            <InfoStrip data-info="pick-range">
              <Info size={15} />
              {t('attendanceDashboard.filters.pickRange')}
            </InfoStrip>
          ) : null}
          {noTeam ? (
            <InfoStrip data-info="no-team">
              <Info size={15} />
              {t('attendanceDashboard.scope.noTeam')}
            </InfoStrip>
          ) : null}
          {beforeRules ? (
            <InfoStrip data-info="rules-effective">
              <Info size={15} />
              {t('attendanceDashboard.rulesNote', { date: formatIsoDay(summary.rulesEffectiveFrom, i18n.language) })}
            </InfoStrip>
          ) : null}
        </StripStack>
      ) : null}
      <Stale $stale={awaitingRange} data-awaiting={awaitingRange ? '1' : undefined}>
        <Grid>
          <KpiRow slice={slices.summary} query={awaitingRange ? null : query} />
          <DailyTrendCard slice={slices.trend} query={drillable ? query : null} effectiveTo={effectiveTo} />
          <DepartmentBreakdownCard
            slice={slices.departments}
            query={query}
            effectiveTo={effectiveTo}
            closedTo={closedTo}
            drillable={drillable}
          />
          <LateDistributionCard slice={slices.lateDistribution} />
          <TopPeopleCard kind="late" slice={slices.topLate} query={query} effectiveTo={effectiveTo} drillable={drillable} />
          <TopPeopleCard
            kind="undertime"
            slice={slices.topUndertime}
            query={query}
            closedTo={closedTo}
            drillable={Boolean(drillable && summary.closedTo >= summary.from)}
          />
          <BradfordCard slice={slices.bradford} />
          <WarningsCard
            slice={slices.warnings}
            canWriteTa={canWrite('TA')}
            canReadSm={canRead('SM') || canWrite('SM')}
          />
        </Grid>
      </Stale>
    </PageWrap>
  );
}
